/**
 * BuildSummative — Standalone Summative chooser (Phase 119.B · Feb 2026).
 *
 * Route: /assess/build
 *
 * Single entry point for teachers who want to create a summative
 * WITHOUT a parent unit. Picks one of three summative types:
 *   - Test (Test Builder, /tests/new?standalone=1)
 *   - Performance Task (uses the existing summative-task pipeline)
 *   - Project (also uses the summative-task pipeline with a project hint)
 *
 * The picker lives under the left rail's Assess & Report section.
 */

import { Link, Navigate } from "react-router-dom";
import { ClipboardCheck, FileText, FlaskConical, Sparkles } from "lucide-react";
import AppShell from "../components/shell/AppShell";
import { useAuth } from "../contexts/AuthContext";

const ChoiceCard = ({ to, icon: Icon, title, blurb, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group flex flex-col gap-3 rounded-xl border-2 border-[#4a6984]/15 bg-white p-6 text-left transition-all hover:border-[#1c2d3d] hover:shadow-lg"
  >
    <div className="flex items-center gap-3">
      <span className="rounded-md bg-[#96ded1]/30 p-2 text-[#1c2d3d]">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-extrabold text-lg text-[#1c2d3d]">{title}</h3>
    </div>
    <p className="text-sm text-[#4a6984] leading-snug">{blurb}</p>
    <p className="mt-auto text-[11px] font-bold uppercase tracking-[0.15em] text-[#1c9e7e] group-hover:text-[#1c2d3d]">
      Start →
    </p>
  </Link>
);

export default function BuildSummative() {
  const { user, loading } = useAuth();
  if (!loading && !user) return <Navigate to="/" replace />;

  return (
    <AppShell title="Build a Summative" subtitle="Choose a summative type">
      <div className="bg-[#f4f5f7] min-h-full">
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#4a6984]/70">
              Assess &amp; Report
            </p>
            <h2 className="font-extrabold text-3xl text-[#1c2d3d]">
              What kind of summative are you building?
            </h2>
            <p className="text-sm text-[#4a6984] max-w-2xl leading-snug">
              You can ground any of these in the actual lessons you&apos;ve
              taught — the builder reads the full lesson content so the
              summative reflects what students experienced, not generic
              curriculum text.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ChoiceCard
              to="/tests/new?standalone=1"
              icon={ClipboardCheck}
              title="Test"
              blurb="A traditional paper test — MC, true/false, matching, short answer, long answer, labeling. Auto-scored objectives, AI-assist marking for written answers."
              testid="build-summative-test"
            />
            <ChoiceCard
              to="/assess/build/performance-task"
              icon={FlaskConical}
              title="Performance Task"
              blurb="A hands-on culminating task — students apply skills across 2+ expectations in one assessment. Comes with a rubric you mark in the rubric panel."
              testid="build-summative-performance-task"
            />
            <ChoiceCard
              to="/assess/build/project"
              icon={FileText}
              title="Project"
              blurb="A multi-period project (2-10 classes) that spans multiple expectations. Generated as a structured summative task you can pace and mark."
              testid="build-summative-project"
            />
          </div>

          <div className="rounded-lg bg-white border border-[#96ded1]/40 p-5 text-sm text-[#4a6984] leading-snug">
            <p className="font-bold text-[#1c2d3d] inline-flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-[#1c9e7e]" />
              Grounding in taught lessons
            </p>
            <p className="mt-1">
              On the next step you&apos;ll see your recently taught lessons.
              Attach the ones this summative should cover — the builder
              will read their full content (examples, vocabulary,
              scenarios) so the questions or task reflect what you
              actually taught.
            </p>
          </div>
        </main>
      </div>
    </AppShell>
  );
}
